import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getMyPermissions, UserPermissions } from '../../entities/user/api/userApi';
import { IsAuthenticatedUseCase } from '../../features/auth';

interface PermissionsContextType {
  permissions: UserPermissions | null;
  loading: boolean;
  hasPageAccess: (page: string) => boolean;
  canEditPage: (page: string) => boolean;
  refreshPermissions: () => Promise<void>;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

export const PermissionsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [permissions, setPermissions] = useState<UserPermissions | null>(null);
  const [loading, setLoading] = useState(true);

  const isAuthenticatedUseCase = new IsAuthenticatedUseCase();

  const loadPermissions = async () => {
    try {
      const isAuthenticated = isAuthenticatedUseCase.execute();

      if (!isAuthenticated) {
        setPermissions({
          is_superuser: false,
          allowed_sites: [],
          allowed_categories: [],
          allowed_pages: [],
          page_permissions: {},
        });
        return;
      }

      const data = await getMyPermissions();
      console.log('Загружены права пользователя:', data);
      setPermissions(data);
    } catch (error) {
      console.error('Ошибка загрузки прав:', error);
      setPermissions({
        is_superuser: false,
        allowed_sites: [],
        allowed_categories: [],
        allowed_pages: [],
        page_permissions: {},
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPermissions();
  }, []);

  const hasPageAccess = (page: string): boolean => {
    if (!permissions) {
      console.log('hasPageAccess: permissions is null for page', page);
      return false;
    }
    if (permissions.is_superuser) {
      console.log('hasPageAccess: user is superuser, allowing', page);
      return true;
    }
    const hasAccess = permissions.allowed_pages.includes(page);
    console.log('hasPageAccess:', page, 'allowed_pages:', permissions.allowed_pages, 'result:', hasAccess);
    return hasAccess;
  };

  const canEditPage = (page: string): boolean => {
    if (!permissions) return false;
    if (permissions.is_superuser) return true;
    return permissions.page_permissions[page] || false;
  };

  return (
    <PermissionsContext.Provider
      value={{
        permissions,
        loading,
        hasPageAccess,
        canEditPage,
        refreshPermissions: loadPermissions,
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = (): PermissionsContextType => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within PermissionsProvider');
  }
  return context;
};

